// Modal showing smart transfer suggestions when a cheque payment exceeds balance.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, AlertTriangle, ArrowRight, Landmark, Coins, Wallet, PiggyBank } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { payWithTransfer } from "@/lib/chequePayment";
import { NoLinkedAccountNotice } from "@/components/NoLinkedAccountNotice";

const ICON = { bank: Landmark, cash: Coins, wallet: Wallet, savings: PiggyBank };
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export function ChequeOverdraftModal() {
  const { lang, t } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const on = (e) => setData(e.detail);
    window.addEventListener("malat:cheque-overdraft", on);
    return () => window.removeEventListener("malat:cheque-overdraft", on);
  }, []);

  if (!data) return null;
  const { cheque, overdraft: od, reload } = data;
  const ccy = od.cheque_currency || cheque.currency;
  const needsLink = od.requires_account_link;
  const sources = od.single_source?.length > 0 ? od.single_source : od.combined_sources || [];
  const canCoverSingle = od.single_source?.length > 0;

  const doPay = async (srcId) => {
    setBusy(true);
    const ok = await payWithTransfer(cheque, srcId, () => { reload?.(); setData(null); }, t("error"));
    if (!ok) setBusy(false);
  };

  return (
    <div data-testid="cheque-overdraft-modal" className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm grid place-items-center p-3" onClick={() => !busy && setData(null)}>
      <div className="w-full max-w-md neu p-5 max-h-[85vh] overflow-y-auto space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 grid place-items-center"><AlertTriangle className="w-5 h-5" /></div>
            <h3 className="font-display font-extrabold text-lg neu-accent-blue">
              {needsLink ? (lang === "ar" ? "الشيك بلا حساب" : "Cheque not linked") : (lang === "ar" ? "رصيد غير كافٍ" : "Insufficient balance")}
            </h3>
          </div>
          <button data-testid="overdraft-close" disabled={busy} onClick={() => setData(null)} className="w-9 h-9 rounded-full neu-sm grid place-items-center"><X className="w-4 h-4" /></button>
        </div>
        {needsLink ? (
          <NoLinkedAccountNotice od={od} ccy={ccy} lang={lang} />
        ) : (
          <div className="p-3 rounded-2xl neu-inset text-sm space-y-1">
            <p className="text-slate-700">{lang === "ar" ? `حساب "${od.account_name}" لا يغطي هذا الشيك.` : `"${od.account_name}" cannot cover this cheque.`}</p>
            <div className="flex justify-between tabular-nums"><span className="text-slate-500">{lang === "ar" ? "الرصيد" : "Balance"}</span><span>{fmt(od.current_balance)} {ccy}</span></div>
            <div className="flex justify-between tabular-nums"><span className="text-slate-500">{lang === "ar" ? "الشيك" : "Cheque"}</span><span>{fmt(od.adding)} {ccy}</span></div>
            <div className="flex justify-between tabular-nums font-bold text-red-600"><span>{lang === "ar" ? "النقص" : "Shortfall"}</span><span>{fmt(od.shortfall)} {ccy}</span></div>
          </div>
        )}
        {!needsLink && (
        <div>
          <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">{lang === "ar" ? (canCoverSingle ? "اقتراحات ذكية للتغطية" : "لا يوجد حساب يغطي كامل النقص") : (canCoverSingle ? "Smart transfer suggestions" : "No single account covers it")}</p>
          {sources.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">{lang === "ar" ? "لا توجد حسابات أخرى بها رصيد." : "No other funded accounts."}</p>
          ) : sources.map((s) => {
            const Ic = ICON[s.type] || Landmark;
            const enough = s.balance >= od.shortfall;
            const foreign = s.currency && s.currency !== ccy;
            return (
              <button key={s.id} data-testid={`transfer-src-${s.id}`} disabled={busy || !enough} onClick={() => doPay(s.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-2xl mb-2 text-start ${enough ? "neu-sm hover:bg-blue-50" : "neu-sm opacity-50 cursor-not-allowed"}`}>
                <div className="w-10 h-10 rounded-full neu-fill-blue text-white grid place-items-center shrink-0 shadow-sm shadow-blue-900/20"><Ic className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{s.name}</p>
                  <p className="text-xs text-slate-500 tabular-nums">
                    {lang === "ar" ? "الرصيد" : "Balance"}: {fmt(s.native_balance ?? s.balance)} {s.currency}
                    {foreign && <span className="opacity-75"> · ≈ {fmt(s.balance)} {ccy}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-xs font-bold" style={{ color: enough ? "#1e3a8a" : "#94a3b8" }}>
                  <span className="tabular-nums">{fmt(od.shortfall)} {ccy}</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>
            );
          })}
        </div>
        )}
        {!needsLink && (
        <p className="text-[11px] text-slate-500 text-center leading-relaxed">
          {lang === "ar" ? "سيتم تحويل المبلغ تلقائيًا إلى حساب الشيك ثم اعتباره مدفوعًا." : "Amount will be transferred to the cheque's account, then the cheque marked paid."}
        </p>
        )}
      </div>
    </div>
  );
}
